<!-- src/components/odontogram/OdontogramPreview.vue -->
<template>
    <div class="odontogram-preview">
        <div v-if="loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>

        <div v-else-if="error" class="alert alert-danger py-2">
            <small>{{ error }}</small>
        </div>

        <div v-else>
            <!-- Full adult odontogram visualization -->
            <div class="simplified-odontogram">
                <!-- Upper jaw -->
                <div class="jaw upper-jaw">
                    <div class="teeth-group">
                        <div v-for="toothNumber in upperTeeth" :key="toothNumber" class="tooth-mini"
                            :class="getToothClass(toothNumber)" :title="getToothTooltip(toothNumber)"></div>
                    </div>
                </div>

                <!-- Lower jaw -->
                <div class="jaw lower-jaw">
                    <div class="teeth-group">
                        <div v-for="toothNumber in lowerTeeth" :key="toothNumber" class="tooth-mini"
                            :class="getToothClass(toothNumber)" :title="getToothTooltip(toothNumber)"></div>
                    </div>
                </div>
            </div>

            <!-- Summary statistics -->
            <div class="odontogram-stats mt-3">
                <div class="row text-center g-2">
                    <div class="col-3">
                        <div class="stat-value">{{ teethStats.healthy }}</div>
                        <div class="stat-label">Sanos</div>
                    </div>
                    <div class="col-3">
                        <div class="stat-value">{{ teethStats.missing }}</div>
                        <div class="stat-label">Ausentes</div>
                    </div>
                    <div class="col-3">
                        <div class="stat-value">{{ teethStats.treatment }}</div>
                        <div class="stat-label">Tratados</div>
                    </div>
                    <div class="col-3">
                        <div class="stat-value">{{ teethStats.needsAttention }}</div>
                        <div class="stat-label">Atención</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { toothService } from '@/services/api';

export default {
    name: 'OdontogramPreview',
    props: {
        odontogramId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            loading: true,
            error: null,
            teeth: [],
            // Full set of adult teeth
            upperTeeth: ['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'],
            lowerTeeth: ['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38']
        };
    },
    computed: {
        // Lista de dientes adultos (del 11 al 48)
        adultTeethNumbers() {
            // Incluye todos los dientes desde el 11 hasta el 48
            return [
                '11', '12', '13', '14', '15', '16', '17', '18',
                '21', '22', '23', '24', '25', '26', '27', '28',
                '31', '32', '33', '34', '35', '36', '37', '38',
                '41', '42', '43', '44', '45', '46', '47', '48'
            ];
        },
        
        // Filtro de dientes solo para adultos
        adultTeeth() {
            return this.teeth.filter(tooth => 
                this.adultTeethNumbers.includes(tooth.tooth_number)
            );
        },
        
        teethStats() {
            // Usar adultTeeth en lugar de this.teeth para las métricas
            const adultsOnly = this.adultTeeth;
            
            return {
                // Sano (solo dientes marcados como "sano", con o sin observaciones)
                healthy: adultsOnly.filter(t => 
                    t.condition === 'sano'
                ).length,
                
                // Ausente (solo dientes marcados como "ausente")
                missing: adultsOnly.filter(t => 
                    t.condition === 'ausente'
                ).length,
                
                // Tratados (endodoncia, implante, restauración, corona)
                treatment: adultsOnly.filter(t =>
                    ['endodoncia', 'implante', 'restauracion', 'corona'].includes(t.condition)
                ).length,
                
                // Atención (caries, extracción indicada - ya no incluye sano con observaciones)
                needsAttention: adultsOnly.filter(t => 
                    ['caries', 'extraccion'].includes(t.condition)
                ).length
            };
        }
    },
    async created() {
        try {
            await this.fetchTeeth();
        } catch (error) {
            console.error('Error loading teeth data:', error);
            this.error = 'No se pudieron cargar los datos del odontograma';
        } finally {
            this.loading = false;
        }
    },
    methods: {
        async fetchTeeth() {
    console.log("Fetching teeth for odontogram ID:", this.odontogramId);
    try {
        // Get teeth for this specific odontogram
        const response = await toothService.getByOdontogram(this.odontogramId);
        console.log("Response data:", response.data);
        
        // Double check the filter on the client side
        this.teeth = response.data.filter(tooth => 
            tooth.odontogram === parseInt(this.odontogramId)
        );
        console.log("Filtered teeth:", this.teeth);

        // Ensure we have data for all teeth
        this.ensureAllTeethExist();
    } catch (error) {
        console.error('Error loading teeth data:', error);
        this.error = 'No se pudieron cargar los datos del odontograma';
    }
},
        ensureAllTeethExist() {
            const allTeethNumbers = [...this.upperTeeth, ...this.lowerTeeth];
            const existingTeethNumbers = this.teeth.map(tooth => tooth.tooth_number);

            // Create default entries for missing teeth
            allTeethNumbers.forEach(toothNumber => {
                if (!existingTeethNumbers.includes(toothNumber)) {
                    this.teeth.push({
                        odontogram: parseInt(this.odontogramId),
                        tooth_number: toothNumber,
                        condition: 'sano',
                        observation: ''
                    });
                }
            });
        },
        getToothClass(toothNumber) {
            const tooth = this.teeth.find(t => t.tooth_number === toothNumber);
            if (!tooth) return 'sano';

            // Añadir clase adicional para dientes sanos con observaciones
            if (tooth.condition === 'sano' && tooth.observation && tooth.observation.trim() !== '') {
                return 'sano-con-observacion';
            }

            const conditionClasses = {
                'sano': 'sano',
                'ausente': 'ausente',
                'extraccion': 'extraccion',
                'caries': 'caries',
                'corona': 'corona',
                'restauracion': 'restauracion',
                'implante': 'implante',
                'endodoncia': 'endodoncia'
            };

            return conditionClasses[tooth.condition] || 'sano';
        },
        getToothTooltip(toothNumber) {
            const tooth = this.teeth.find(t => t.tooth_number === toothNumber);
            if (!tooth) return `Diente ${toothNumber}: Sano`;

            const conditionLabels = {
                'sano': 'Sano',
                'ausente': 'Ausente',
                'extraccion': 'Extracción Indicada',
                'caries': 'Caries',
                'corona': 'Corona',
                'restauracion': 'Restauración',
                'implante': 'Implante',
                'endodoncia': 'Endodoncia'
            };

            let tooltip = `Diente ${toothNumber}: ${conditionLabels[tooth.condition] || 'Estado desconocido'}`;

            if (tooth.observation) {
                tooltip += ` - ${tooth.observation}`;
            }

            return tooltip;
        }
    }
};
</script>

<style scoped>
.odontogram-preview {
    padding: 10px;
}

.simplified-odontogram {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.jaw {
    width: 100%;
    padding: 5px;
    margin-bottom: 5px;
    background-color: #f8f9fa;
    border-radius: 5px;
}

.teeth-group {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.tooth-mini {
    width: 16px;
    height: 16px;
    border: 1px solid #495057;
    border-radius: 3px;
    cursor: pointer;
    margin: 0 1px;
}

/* Tooth conditions with pastel colors */
.tooth-mini.sano { 
    background-color: #ffffff; 
}

/* Nueva clase para dientes sanos con observaciones */
.tooth-mini.sano-con-observacion { 
    background-color: #ffffff; 
    border-color: #ffc107;
    border-style: dashed;
}

.tooth-mini.ausente { 
    background-color: #d3d3d3; 
    border-style: dashed;
}

.tooth-mini.extraccion {
    background-color: #ff9a8c;
    border-color: #dc3545;
}

.tooth-mini.caries {
    background-color: #fffacd;
    border-color: #ffc107;
}

.tooth-mini.corona {
    background-color: #c1e1c1;
    border-color: #198754;
}

.tooth-mini.restauracion {
    background-color: #add8e6;
    border-color: #0d6efd;
}

.tooth-mini.implante {
    background-color: #ffdab9;
    border-color: #fd7e14;
}

.tooth-mini.endodoncia {
    background-color: #ffc0cb;
    border-color: #e83e8c;
}

.odontogram-stats {
    border-top: 1px solid #dee2e6;
    padding-top: 10px;
}

.stat-value {
    font-weight: bold;
    font-size: 1.1rem;
}

.stat-label {
    color: #6c757d;
    font-size: 0.8rem;
    white-space: nowrap;
    /* Prevent word breaking */
}

.odontogram-stats .col-3 {
    min-width: 70px;
    /* Ensure enough space for labels */
}
</style>